import { useEffect, useRef } from 'react';
import { appConfig } from '@/config/app';

export type RefreshTier = 'liveTicker' | 'marketSnapshot' | 'geographyCache' | 'fullRevalidate';

/**
 * Drives the tiered refresh cadence described in the Phase 1 spec:
 *  - liveTicker (5m): cheap check for "is anything newer available"
 *  - marketSnapshot (1h): re-check + re-parse the static snapshot files
 *  - geographyCache (12h): boundary files rarely change, check rarely
 *  - fullRevalidate (24h): force a full re-fetch + rescore regardless
 * All four tiers currently invoke the same `onTick` (a full pipeline
 * refresh); the separate intervals exist so a future version can make
 * liveTicker cheaper (e.g. a HEAD request against manifest.json) without
 * restructuring this hook.
 */
export function useRefreshScheduler(onTick: (tier: RefreshTier) => void) {
  const savedCallback = useRef(onTick);
  savedCallback.current = onTick;

  useEffect(() => {
    const timers: Array<ReturnType<typeof setInterval>> = [];
    (Object.keys(appConfig.refreshIntervalsMs) as RefreshTier[]).forEach((tier) => {
      const ms = appConfig.refreshIntervalsMs[tier];
      timers.push(
        setInterval(() => {
          savedCallback.current(tier);
        }, ms),
      );
    });
    return () => timers.forEach(clearInterval);
  }, []);
}
